// useEnsureSummaries — Phase 6i.
//
// Standup loads a bucket of conversations, then posts their ids to the
// backend which returns a one-sentence Haiku summary for each (regenerating
// any that are stale). This hook wraps that call so Standup can render the
// summary in place of the raw subject.
//
// Notes:
// - We keep the query enabled only when the ids list is non-empty.
// - Stale-while-revalidate cache is 5 min; on a hard-refresh we do a fresh
//   fetch, on route re-entry within 5 min we show stored values.
// - Failures are silent (the UI falls back to the subject line), so the
//   hook's error shape isn't surfaced to consumers.
import { useQuery } from '@tanstack/react-query';
import { apiPost } from '@/lib/api';

export type SummaryEntry = {
  summary: string;
  highlight: string | null;
};

export type SummariesMap = Record<string, SummaryEntry | null>;

type SummariesResponse = {
  summaries: SummariesMap;
};

export function useEnsureSummaries(ids: string[]) {
  // Stable key: sort ids so order-swaps don't refetch. JSON.stringify is fine
  // for ≤60 uuids — orders of magnitude cheaper than the HTTP call.
  const stableIds = [...ids].sort();
  return useQuery<SummariesResponse>({
    queryKey: ['conversation-summaries', stableIds],
    enabled: stableIds.length > 0,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    queryFn: () =>
      apiPost<SummariesResponse, { ids: string[] }>('/api/conversations/summaries', {
        ids: stableIds,
      }),
  });
}
